import * as React from "react";
import { ChevronLeft, ChevronRight, HandCoins, Wallet } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import {
  ADJUSTMENT_LABELS,
  formatRupees,
  istToday,
  monthKey,
  monthTitle,
  parseDay,
  shiftMonth,
  shortDay,
  useMyTeamPay,
} from "../../lib/team/api";

/** Phase SC-22d — what a team member earned, was paid and is still owed. */

const METHOD: Record<string, string> = { cash: "Cash", upi: "UPI", bank: "Bank", other: "Other" };

export function TeamPay() {
  const [ty, tm] = parseDay(istToday());
  const [[year, month], setYm] = React.useState<[number, number]>([ty, tm]);
  const query = useMyTeamPay(monthKey(year, month));
  const pay = query.data;
  const isCurrent = year === ty && month === tm;

  return (
    <div className="space-y-5">
      <Reveal>
        <div className="flex items-center justify-between gap-3">
          <SectionHeader title="Pay" level={1} />
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-9 rounded-full"
              aria-label="Previous month"
              onClick={() => setYm(shiftMonth(year, month, -1))}
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </Button>
            <span className="min-w-28 text-center text-sm font-medium" aria-live="polite">
              {monthTitle(year, month)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="size-9 rounded-full"
              aria-label="Next month"
              disabled={isCurrent}
              onClick={() => setYm(shiftMonth(year, month, 1))}
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </div>
      </Reveal>

      {query.isError ? (
        <SectionCard role="alert" className="flex items-center justify-between gap-3 text-sm">
          <span className="text-destructive">Couldn&apos;t load your pay.</span>
          <Button size="sm" variant="outline" onClick={() => query.refetch()}>
            Retry
          </Button>
        </SectionCard>
      ) : null}

      {query.isLoading || !pay ? (
        query.isLoading ? (
          <SectionCard className="space-y-3" aria-hidden="true">
            <div className="bg-muted h-8 w-40 rounded motion-safe:animate-pulse" />
            <div className="bg-muted h-4 w-full rounded motion-safe:animate-pulse" />
            <div className="bg-muted h-4 w-2/3 rounded motion-safe:animate-pulse" />
          </SectionCard>
        ) : null
      ) : (
        <>
          <Reveal index={1}>
            <SectionCard className="space-y-4" data-testid="team-pay-card">
              <div className="flex items-start gap-3">
                <span className="bg-primary/12 text-primary grid size-12 shrink-0 place-items-center rounded-2xl">
                  <Wallet aria-hidden="true" className="size-6" />
                </span>
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs">
                    {isCurrent ? "Owed to you today" : "Still owed for the month"}
                  </p>
                  <p className="font-display text-3xl font-semibold tabular-nums">
                    {formatRupees(Math.max(0, pay.owed_today_paise))}
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">{pay.explanation}</p>
              <dl className="divide-border/60 divide-y text-sm">
                {(
                  [
                    ["Earned", pay.earned_paise, false],
                    ["Added", pay.additions_paise, false],
                    ["Taken off", pay.deductions_paise + pay.recovery_paise, true],
                    ["Paid", pay.paid_paise, true],
                  ] as const
                ).map(([label, amount, subtracts]) => (
                  <div key={label} className="flex items-center justify-between py-2">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="tabular-nums">
                      {subtracts && amount > 0 ? `− ${formatRupees(amount)}` : formatRupees(amount)}
                    </dd>
                  </div>
                ))}
              </dl>
            </SectionCard>
          </Reveal>

          {pay.advance_outstanding_paise > 0 ? (
            <Reveal index={2}>
              <SectionHeader title="Advances" />
              <SectionCard className="space-y-3">
                <p className="flex items-center gap-2 text-sm">
                  <HandCoins aria-hidden="true" className="text-primary size-4" />
                  {formatRupees(pay.advance_outstanding_paise)} still to come out of your pay.
                </p>
                <ul className="space-y-2">
                  {pay.advances
                    .filter((a) => a.outstanding_paise > 0)
                    .map((a) => (
                      <li key={a.id} className="bg-muted/40 rounded-xl px-3 py-2 text-xs">
                        <p className="text-sm font-medium tabular-nums">
                          {formatRupees(a.amount_paise)} on {shortDay(a.given_on)}
                        </p>
                        <p className="text-muted-foreground">
                          {a.schedule
                            .map(
                              (s) =>
                                `${monthTitle(s.year, s.month).split(" ")[0]?.slice(0, 3)} ${formatRupees(s.amount_paise)}`,
                            )
                            .join(" · ")}
                        </p>
                      </li>
                    ))}
                </ul>
              </SectionCard>
            </Reveal>
          ) : null}

          {pay.adjustments.length > 0 || pay.payments.length > 0 ? (
            <Reveal index={3}>
              <SectionHeader title="This month" />
              <SectionCard className="divide-border/60 divide-y p-0">
                {pay.adjustments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{ADJUSTMENT_LABELS[a.kind]}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {shortDay(a.effective_on)}
                        {a.note ? ` · ${a.note}` : ""}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm tabular-nums">
                      {a.kind === "deduction" ? "− " : "+ "}
                      {formatRupees(a.amount_paise)}
                    </span>
                  </div>
                ))}
                {pay.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Paid · {METHOD[p.method] ?? p.method}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {shortDay(p.paid_on)} · {p.allocation_summary}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm tabular-nums">
                      {formatRupees(p.amount_paise)}
                    </span>
                  </div>
                ))}
              </SectionCard>
            </Reveal>
          ) : null}

          {pay.payslips.length > 0 ? (
            <Reveal index={4}>
              <SectionHeader title="Payslips" />
              <SectionCard className="divide-border/60 divide-y p-0">
                {pay.payslips.map((s) => (
                  <div
                    key={s.pay_run_id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <p className="text-sm font-medium">{monthTitle(s.year, s.month)}</p>
                    <span className="text-sm tabular-nums">{formatRupees(s.net_paise)}</span>
                  </div>
                ))}
              </SectionCard>
            </Reveal>
          ) : null}
        </>
      )}
    </div>
  );
}
